import React, { useEffect } from 'react';
import { formatDate } from '../utils/dateFormat';
import { Logo } from '../components/Logo';
import { LegalFooter } from '../components/LegalFooter';
import { ThemeToggle } from '../components/ThemeToggle';
import { Button } from '@/components/ui/button';

function PrivacyPage() {
  useEffect(() => {
    document.title = 'Privacy Policy | PATH by Secret Sauce';
  }, []);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b border-border bg-card">
        <div className="container-custom py-4">
          <div className="flex items-center justify-between">
            <a href="/">
              <Logo variant="full" size="default" />
            </a>
            <div className="flex items-center gap-3">
              <ThemeToggle />
              <Button asChild variant="ghost" size="sm">
                <a href="/login">Login</a>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container-custom py-12 max-w-4xl flex-1">
        <h1 className="h1 mb-8">Privacy Policy</h1>
        
        <div className="prose prose-slate dark:prose-invert max-w-none">
          <p className="body text-lg mb-6">
            Last updated: {formatDate(new Date())}
          </p>

          <p className="body mb-6">
            Secret Sauce ("we", "our", or "us") operates the PATH Programme platform. 
            This page informs you of our policies regarding the collection, use, and 
            disclosure of personal data when you use our Service.
          </p>

          <h2 className="h2 text-2xl mt-8 mb-4">1. Information We Collect</h2>
          <p className="body mb-4">
            We collect information you provide directly to us, including:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2 body">
            <li>Email address and name</li>
            <li>Professional information (job title, organisation, years of experience)</li>
            <li>Responses to our EDGE and CORE diagnostics</li>
            <li>Application information if you apply for the PATH Programme</li>
            <li>Communications you send to us</li>
          </ul>

          <h2 className="h2 text-2xl mt-8 mb-4">2. How We Use Your Information</h2>
          <p className="body mb-4">
            We use the information we collect to:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2 body">
            <li>Provide, maintain, and improve our diagnostics and programmes</li>
            <li>Deliver personalised assessment results and recommendations</li>
            <li>Process your PATH Programme application</li>
            <li>Send you relevant communications about your journey</li>
            <li>Respond to your comments, questions, and requests</li>
          </ul>

          <h2 className="h2 text-2xl mt-8 mb-4">3. Information Sharing</h2>
          <p className="body mb-4">
            We do not sell, trade, or rent your personal information to third parties. 
            We may share your information only:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2 body">
            <li>With your explicit consent</li>
            <li>To comply with legal obligations</li>
            <li>To protect our rights and safety</li>
            <li>With service providers who assist in operating our platform (under strict data processing agreements)</li>
          </ul>

          <h2 className="h2 text-2xl mt-8 mb-4">4. Data Security</h2>
          <p className="body mb-4">
            We implement appropriate technical and organisational security measures to protect 
            your personal information against unauthorised access, alteration, disclosure, or destruction. 
            This includes encryption in transit and at rest, access controls, and regular security assessments.
          </p>

          <h2 className="h2 text-2xl mt-8 mb-4">5. Your Rights (GDPR)</h2>
          <p className="body mb-4">
            Under the General Data Protection Regulation (GDPR), you have the following rights:
          </p>
          <ul className="list-disc pl-6 mb-4 space-y-2 body">
            <li><strong>Access:</strong> Request a copy of your personal data</li>
            <li><strong>Rectification:</strong> Request correction of inaccurate data</li>
            <li><strong>Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
            <li><strong>Portability:</strong> Request your data in a machine-readable format</li>
            <li><strong>Objection:</strong> Object to processing of your data</li>
          </ul>
          <p className="body mb-4">
            You can exercise these rights through your dashboard settings, or by contacting us directly.
          </p>

          <h2 className="h2 text-2xl mt-8 mb-4">6. Data Retention</h2>
          <p className="body mb-4">
            We retain your personal data only for as long as necessary to fulfil the purposes 
            outlined in this policy, unless a longer retention period is required by law. 
            You can request deletion of your data at any time.
          </p>

          <h2 className="h2 text-2xl mt-8 mb-4">7. Contact Us</h2>
          <p className="body mb-4">
            If you have questions about this Privacy Policy or wish to exercise your data rights, 
            please contact us at:
          </p>
          <p className="body mb-4">
            <strong>Email:</strong> hello@secretsauce.fyi<br />
            <strong>Company:</strong> Organisation Logic Limited<br />
            <strong>Registration:</strong> England & Wales Company Registration Number: 12213127
          </p>
        </div>
      </main>

      <LegalFooter />
    </div>
  );
}

export default PrivacyPage;
